import { useCampaignCode } from '@campaign-services/hooks/useCampaignCode'
import { type CountryCode, type ProjectCode } from '@constants'
import { useFastpassCampaignGoogleAnalyticEvent } from '@dni-analysis'
import { type Variation } from '@hooks-api/types/dataModel/products'
import { Cross } from '../Icon/icon'
import { ProductInfoDisplay } from '../ProductInfoDisplay'
import TitleBar from '../TitleBar/TitleBar'
import style from './SearcherSearchResultsDisplay.module.scss'
import { useLegacySearcherConfig } from './useLegacySearcherConfig'

interface SearcherSearchResultsDisplayProps {
  products: Variation[]
  onToggle: () => void
  tryFetchNextPage: () => void
  keyword: string
  projectCode: ProjectCode
  campaignId: number
  environment?: 'campaign' | 'merchant'
  countryCode: CountryCode
  onResultClick?: (product: Variation) => void
}

const SearcherSearchResultsDisplay = ({
  products,
  onToggle,
  tryFetchNextPage,
  keyword,
  projectCode,
  campaignId,
  environment,
  countryCode,
  onResultClick,
}: SearcherSearchResultsDisplayProps) => {
  const { campaignCodeFromUrl } = useCampaignCode()
  const { useProductsData, clickableHandler } = useLegacySearcherConfig(projectCode, campaignCodeFromUrl, campaignId, onToggle)
  const { productsData } = useProductsData()
  const { sendClickSearchResultEvent } = useFastpassCampaignGoogleAnalyticEvent({ projectCode, countryCode, campaignId })

  const handleScroll = (e: React.UIEvent<HTMLUListElement>) => {
    if (e === null) {
      return
    }
    const target = e.target as HTMLElement | null
    if (target === null) {
      return
    }
    const epsilon = 1 // Tolerance value
    if (target.scrollHeight - target.scrollTop <= target.clientHeight + epsilon) {
      // scroll to bottom?
      // Bottom of the element reached or close enough
      tryFetchNextPage()
    }
  }

  const titleBarInfo = (
    <div className={style.title}>
      <h3 className={style.title}>
        搜尋
        <span className={style['highlighted-keyword']}>{`『${keyword.length > 10 ? keyword.substring(0, 11) + '...' : keyword}』`}</span>
        的結果：
      </h3>
    </div>
  )
  const titleBarButton = (
    <button
      onClick={() => {
        onToggle()
      }}
    >
      <Cross />
    </button>
  )

  const handleClickableAreaClick = (product: Variation) => {
    if (environment === 'campaign') {
      sendClickSearchResultEvent({
        productSku: product.sku,
        productRealSku: product.real_sku,
        variationId: product.variation_id,
        productName: product.name,
      })
    }
    clickableHandler(productsData, product)
    onResultClick?.(product)
  }

  return (
    <div>
      <TitleBar infoComponent={titleBarInfo} buttonComponent={titleBarButton} />
      <ul
        className={style['scrollable-list']}
        style={{ height: window.innerHeight * 0.6 }}
        onScroll={handleScroll}
      >
        {products.map((product) => {
          return (
            <ProductInfoDisplay
              keyword={keyword}
              isZoomInAllowed={false}
              variation={product}
              key={product.id}
              clickableConfig={{
                clickableMode: 'WHOLE_DISPLAY_CLICABLE',
                clickableHandler: () => handleClickableAreaClick(product),
              }}
            />
          )
        })}
      </ul>
    </div>
  )
}

export default SearcherSearchResultsDisplay
